/**
 * T9 / audit C7: Data Access Rules — Hub + Division gating for Market users.
 *
 * The Admin > Access Rules card lets an admin create rules of the form
 * { id, hub, divisions[] }. Since audit C7 the rules are persisted via
 * the active DataProvider (GoogleSheets uses Data_Access_Rules sheet,
 * Mock uses its own localStorage key, Excel throws). When the provider
 * call fails for any reason we fall back to the legacy localStorage
 * cache so the admin still has a working UI offline.
 *
 * The acceptance test for this filter (`appliesAccessRule`) is the
 * exported pure function below — it can be unit-tested without the DOM.
 */

import { getState, subscribe } from "../state";
import { unique } from "./shared";
import { escapeHtml } from "../utils/escapeHtml";
import { showToast } from "../components/Toast";
import { getProvider } from "../providerInstance";

const STORAGE_KEY = "ecf-access-rules-v1";

export interface AccessRule {
  id: string;
  hub: string;
  divisions: string[];
}

// ── Pure logic ──────────────────────────────────────────────

/**
 * Returns true when the asset is allowed under the given access rule set.
 * Empty rule set = allow all (admin scope). When at least one rule exists,
 * the asset must match (hub === rule.hub AND division ∈ rule.divisions)
 * for at least one rule.
 */
export function appliesAccessRule(
  asset: { hub?: string; division?: string },
  rules: AccessRule[],
): boolean {
  if (!rules.length) return true;
  return rules.some(
    (r) =>
      asset.hub === r.hub &&
      (r.divisions.length === 0 || r.divisions.includes(asset.division || "")),
  );
}

/**
 * Filter `candidates` down to the hubs the user is allowed to act on.
 * Empty rule list == admin scope → returns a copy of `candidates` as-is.
 * Division filtering lives in `appliesAccessRule`; this helper only cares
 * about hub membership.
 */
export function allowedCountries(
  rules: readonly AccessRule[],
  candidates: readonly string[],
): string[] {
  if (rules.length === 0) return [...candidates];
  const allowed = new Set(rules.map((r) => r.hub));
  return candidates.filter((c) => allowed.has(c));
}

// ── Persistence ─────────────────────────────────────────────
//
// audit M4: corruption / quota / private-mode failures used to be
// silently swallowed. We now emit a console.warn AND remember the
// failure so the next render() can surface a toast to the user.

let storageWarning: string | null = null;

export function consumeStorageWarning(): string | null {
  const w = storageWarning;
  storageWarning = null;
  return w;
}

function loadRules(): AccessRule[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      console.warn(
        "[accessRules] localStorage payload is not an array, ignoring it.",
      );
      storageWarning = "Access rules storage was malformed and has been reset.";
      return [];
    }
    return parsed;
  } catch (err) {
    console.warn("[accessRules] failed to read localStorage:", err);
    storageWarning = "Access rules storage is unavailable — running in memory only.";
    return [];
  }
}

function saveRules(rules: AccessRule[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rules));
  } catch (err) {
    console.warn("[accessRules] failed to write localStorage:", err);
    storageWarning = "Access rules could not be persisted (storage quota or private mode).";
  }
}

let rules: AccessRule[] = loadRules();
// audit C7: source of truth for the badge shown above the table.
// Flips to "remote" the first time a provider call succeeds, flips back
// to "local" on the next failure. Initial value is "unknown" until the
// first refreshFromProvider() call completes.
type StorageMode = "unknown" | "remote" | "local";
let storageMode: StorageMode = "unknown";

export function getAccessRules(): readonly AccessRule[] {
  return rules;
}

// audit C7: refresh rules from the active provider. Called at init() and
// after every successful mutation. Falls back to the localStorage cache
// when the provider throws (ExcelProvider stub, offline, etc.).
async function refreshFromProvider(): Promise<void> {
  try {
    const provider = getProvider();
    const remote = await provider.listDataAccessRules();
    rules = Array.isArray(remote) ? remote : [];
    saveRules(rules); // keep local cache warm for offline fallback
    storageMode = "remote";
  } catch (err) {
    console.warn("[accessRules] provider listDataAccessRules failed, using local cache:", err);
    storageMode = "local";
  }
}

// ── DOM wiring ──────────────────────────────────────────────

export function init(): void {
  const card = document.getElementById("access-rules-card");
  if (!card) return;

  const addBtn = document.getElementById("ar-add-btn");
  const saveBtn = document.getElementById("ar-save-btn");
  const cancelBtn = document.getElementById("ar-cancel-btn");
  const formCard = document.getElementById("ar-form-card");
  const hubSelect = document.getElementById("ar-hub") as HTMLSelectElement | null;
  const divSelect = document.getElementById("ar-divisions") as HTMLSelectElement | null;

  addBtn?.addEventListener("click", () => {
    populateFormSelects();
    formCard?.classList.remove("hidden");
  });
  cancelBtn?.addEventListener("click", () => {
    formCard?.classList.add("hidden");
  });
  saveBtn?.addEventListener("click", async () => {
    const hub = (hubSelect?.value || "").trim();
    if (!hub) return;
    const divisions = divSelect
      ? Array.from(divSelect.selectedOptions).map((o) => o.value)
      : [];
    const newRule: AccessRule = { id: `dar-${Date.now()}`, hub, divisions };

    // Try provider first, fall back to local-only if the call fails.
    let persistedRemote = false;
    try {
      const provider = getProvider();
      const saved = await provider.upsertDataAccessRule(newRule);
      if (saved?.id) newRule.id = saved.id;
      persistedRemote = true;
    } catch (err) {
      console.warn("[accessRules] upsertDataAccessRule failed, saving locally only:", err);
      storageMode = "local";
    }

    rules = [...rules, newRule];
    saveRules(rules);
    if (persistedRemote) storageMode = "remote";
    formCard?.classList.add("hidden");
    renderTable();
    if (!persistedRemote) {
      showToast("Rule saved locally only — provider did not acknowledge.", "error");
    } else {
      showToast("Access rule saved.", "success");
    }
  });

  document.getElementById("ar-table-body")?.addEventListener("click", async (event) => {
    const btn = (event.target as HTMLElement).closest("button[data-ar-delete]");
    if (!btn) return;
    const id = (btn as HTMLElement).dataset.arDelete || "";

    try {
      const provider = getProvider();
      await provider.deleteDataAccessRule(id);
    } catch (err) {
      console.warn("[accessRules] deleteDataAccessRule failed, removing locally only:", err);
    }

    rules = rules.filter((r) => r.id !== id);
    saveRules(rules);
    renderTable();
  });

  subscribe("assets", () => {
    renderTable();
    if (!formCard?.classList.contains("hidden")) populateFormSelects();
  });

  // audit C7: try to hydrate from the provider at startup so the table
  // reflects the shared state immediately. If it fails, the localStorage
  // cache is already loaded.
  refreshFromProvider().finally(renderTable);
}

function populateFormSelects(): void {
  const state = getState();
  const hubs = unique(state.assets.map((a: any) => a.hub).filter(Boolean));
  const divisions = unique(state.assets.map((a: any) => a.division).filter(Boolean));

  const hubSelect = document.getElementById("ar-hub") as HTMLSelectElement | null;
  if (hubSelect) {
    hubSelect.innerHTML =
      `<option value="">Select hub</option>` +
      hubs.map((h: string) => `<option value="${escapeHtml(h)}">${escapeHtml(h)}</option>`).join("");
  }
  const divSelect = document.getElementById("ar-divisions") as HTMLSelectElement | null;
  if (divSelect) {
    divSelect.innerHTML = divisions
      .map((d: string) => `<option value="${escapeHtml(d)}">${escapeHtml(d)}</option>`)
      .join("");
  }
}

// audit C7: injects/updates a status badge above the access rules table so
// the admin knows whether the rules they see (and modify) are remote and
// shared, or stored only in their browser's localStorage.
function renderStorageBadge(): void {
  const card = document.getElementById("access-rules-card");
  if (!card) return;
  let badge = card.querySelector<HTMLElement>(".ar-storage-badge");
  if (!badge) {
    badge = document.createElement("span");
    badge.className = "ar-storage-badge";
    const header = card.querySelector(".card-header");
    header?.appendChild(badge);
  }
  const label =
    storageMode === "remote"
      ? "Shared via provider"
      : storageMode === "local"
        ? "Stored locally — not shared"
        : "Syncing…";
  badge.textContent = label;
  badge.dataset.mode = storageMode;
}

function renderTable(): void {
  const body = document.getElementById("ar-table-body");
  if (!body) return;
  // M4: surface any pending storage warning the next time we render
  const warning = consumeStorageWarning();
  if (warning) showToast(warning, "error");
  // audit C7: show a badge reflecting whether we're using remote or local
  renderStorageBadge();
  if (!rules.length) {
    body.innerHTML = `<tr><td colspan="3" style="color:var(--muted)">No access rules — all Market users see all data.</td></tr>`;
    return;
  }
  body.innerHTML = rules
    .map(
      (r) => `<tr>
        <td>${escapeHtml(r.hub)}</td>
        <td>${r.divisions.length ? r.divisions.map((d) => escapeHtml(d)).join(", ") : "<em>any</em>"}</td>
        <td><button type="button" class="btn btn-sm btn-ghost" data-ar-delete="${escapeHtml(r.id)}">Remove</button></td>
      </tr>`,
    )
    .join("");
}
